import moment from 'moment';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { styles, renderRange } from '../../../utils/common';
import DropdownIndicator from '../DropdownIndicator';

const buildMonthOptions = ({ selectedYear, ageLimit, limitToToday, restrictBoundaryOptions }) => {
  const year = parseInt(selectedYear, 10);
  const baseMonths = renderRange(1, 12, false);

  if (!year) {
    return baseMonths;
  }

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  if (limitToToday && year === currentYear) {
    return baseMonths.filter((option) => parseInt(option.value, 10) <= currentMonth);
  }

  if (restrictBoundaryOptions && ageLimit && year === currentYear - 18) {
    return baseMonths.filter((option) => parseInt(option.value, 10) <= currentMonth);
  }

  if (restrictBoundaryOptions && ageLimit && Math.abs(currentYear - year) === 60) {
    return baseMonths.filter((option) => parseInt(option.value, 10) >= currentMonth);
  }

  return baseMonths;
};

const buildDayOptions = ({ selectedYear, selectedMonth, ageLimit, limitToToday, restrictBoundaryOptions }) => {
  const year = parseInt(selectedYear, 10);
  const month = parseInt(selectedMonth, 10);
  const baseDays = renderRange(1, 31, false);

  if (!year || !month) {
    return baseDays;
  }

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  if (limitToToday && year === currentYear && month === currentMonth) {
    return baseDays.filter((option) => parseInt(option.value, 10) <= currentDay);
  }

  if (restrictBoundaryOptions && ageLimit && year === currentYear - 18 && month === currentMonth) {
    return baseDays.filter((option) => parseInt(option.value, 10) <= currentDay);
  }

  if (restrictBoundaryOptions && ageLimit && Math.abs(currentYear - year) === 60 && month === currentMonth) {
    return baseDays.filter((option) => parseInt(option.value, 10) > currentDay);
  }

  return baseDays;
};

function InputDate(props) {
  const { register, errors, getValues, setValue, setError, selectedDate, color, disabled, onChangeYear, fields, required = true, ageLimit = true, limitToToday = false, restrictBoundaryOptions = true } = props;
  const dayField = fields?.day ?? 'date_of_birth1';
  const monthField = fields?.month ?? 'date_of_birth2';
  const yearField = fields?.year ?? 'date_of_birth3';
  const requiredRule = required ? { required: true } : {};
  const yearEnd = ageLimit ? moment().subtract(18, 'years').format('YYYY') : moment().format('YYYY');
  const [years] = useState(renderRange(moment().subtract(60, 'years').format('YYYY'), yearEnd, true))
  const [defaultYear, setDefaultYear] = useState(null);
  const [defaultMonth, setDefaultMonth] = useState(null);
  const [defaultDay, setDefaultDay] = useState(null);
  const months = buildMonthOptions({ selectedYear: defaultYear?.value, ageLimit, limitToToday, restrictBoundaryOptions });
  const days = buildDayOptions({ selectedYear: defaultYear?.value, selectedMonth: defaultMonth?.value, ageLimit, limitToToday, restrictBoundaryOptions });

  useEffect(() => {
    if (selectedDate) {
      const arr = selectedDate.split('/')
      const nextYearValue = arr[0] + '';
      const nextMonthValue = Number(arr[1]) + '';
      const nextDayValue = Number(arr[2]) + '';
      const nextMonths = buildMonthOptions({ selectedYear: nextYearValue, ageLimit, limitToToday, restrictBoundaryOptions });
      const nextDays = buildDayOptions({ selectedYear: nextYearValue, selectedMonth: nextMonthValue, ageLimit, limitToToday, restrictBoundaryOptions });
      const nextYear = years.find(t => t.value === nextYearValue) ?? null;
      const nextMonth = nextMonths.find(t => t.value === nextMonthValue) ?? null;
      const nextDay = nextMonth ? nextDays.find(t => t.value === nextDayValue) ?? null : null;

      setDefaultYear(nextYear)
      setDefaultMonth(nextMonth)
      setDefaultDay(nextDay)

      setValue(yearField, nextYearValue)
      setValue(monthField, nextMonth?.value ?? null)
      setValue(dayField, nextDay?.value ?? null)
    }
  }, [ageLimit, dayField, limitToToday, monthField, restrictBoundaryOptions, selectedDate, setValue, yearField, years]);

  const checkDate = (dd, mm, yyyy) => {
    if (dd && mm && yyyy) {
      const newDate = moment(`${yyyy}/${mm}/${dd}`, 'YYYY/MM/DD');
      return newDate.isValid();
    }
    return true;
  };

  return (
    <>
      <div className="flex items-center">
        <Select
          placeholder="年"
          className={`flex-1 pl-1 outline-none text-${color ?? 'primary'} border-b border-${color ?? 'primary'} w-full text-center`}
          theme={(theme) => ({
            ...theme,
            borderRadius: 0,
          })}
          styles={{
            ...styles,
            singleValue: (base) => ({
              ...base,
              color: color ?? '#FD9F40',
            }),
            dropdownIndicator: (base) => ({
              ...base,
              color: color ?? '#FD9F40',
              padding: 0
            })
          }}
          value={defaultYear}
          {...register(yearField, requiredRule)}
          onChange={(e) => {
            setDefaultYear(years.find(t => t.value === e.value))
            setValue(yearField, e.value);
            setError(yearField, null);
            if (limitToToday || (ageLimit && restrictBoundaryOptions)) {
              setDefaultMonth(null);
              setDefaultDay(null);
              setValue(monthField, null);
              setValue(dayField, null);
            }
            if(onChangeYear){
              onChangeYear(e.value);
            }
          }}
          options={years}
          isSearchable={false}
          components={{ DropdownIndicator }}
          isDisabled={disabled}
        />
        <Select
          placeholder="月"
          className={`flex-1 pl-1 outline-none text-${color ?? 'primary'} w-full text-center ml-3 mr-3 border-b border-${color ?? 'primary'}`}
          theme={(theme) => ({
            ...theme,
            borderRadius: 0,
          })}
          value={defaultMonth}
          styles={{
            ...styles,
            singleValue: (base) => ({
              ...base,
              color: color ?? '#FD9F40',
            }),
            dropdownIndicator: (base) => ({
              ...base,
              color: color ?? '#FD9F40',
              padding: 0
            })
          }}
          {...register(monthField, requiredRule)}
          onChange={(e) => {
            setDefaultMonth(months.find(t => t.value === e.value))
            setValue(monthField, e.value);
            setError(monthField, null);
            if (limitToToday || (ageLimit && restrictBoundaryOptions)) {
              setDefaultDay(null);
              setValue(dayField, null);
            }
          }}
          options={months}
          isSearchable={false}
          components={{ DropdownIndicator }}
          isDisabled={disabled}
        />
        <Select
          placeholder="日"
          className={`flex-1 pl-1 outline-none text-${color ?? 'primary'} w-full border-b text-center`}
          theme={(theme) => ({
            ...theme,
            borderRadius: 0,
          })}
          value={defaultDay}
          styles={{
            ...styles,
            singleValue: (base) => ({
              ...base,
              color: color ?? '#FD9F40',
            }),
            dropdownIndicator: (base) => ({
              ...base,
              color: color ?? '#FD9F40',
              padding: 0
            })
          }}
          {...register(dayField, requiredRule)}
          onChange={(e) => {
            setDefaultDay(days.find(t => t.value === e.value))
            setValue(dayField, e.value);
            setError(dayField, null);
          }}
          options={days}
          isSearchable={false}
          components={{ DropdownIndicator }}
          isDisabled={disabled}
        />
      </div>
      {(errors?.[dayField] || errors?.[monthField] || errors?.[yearField]) &&
        (errors?.[dayField]?.type === 'required' || errors?.[monthField]?.type === 'required' || errors?.[yearField]?.type === 'required') && (
          <span className="text-10 text-red-500">*必填</span>
        )}
      {!checkDate(getValues(dayField), getValues(monthField), getValues(yearField)) &&
        (!(errors?.[dayField] || errors?.[monthField] || errors?.[yearField]) ||
          !(errors?.[dayField]?.type === 'required' || errors?.[monthField]?.type === 'required' || errors?.[yearField]?.type === 'required')) && (
          <span className="text-10 text-red-500">*无效的</span>
        )}
    </>
  );
}

export default InputDate;
